import React from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  CircularProgress, 
  FormHelperText,
  makeStyles
} from '@material-ui/core';
import { useApi, configApiRef, fetchApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { useAsync } from 'react-use';

// Simple semver utilities for tags like X.Y.Z or vX.Y.Z (supports prerelease and build metadata), case-insensitive 'v'
const SEMVER_RE = /^v?\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?(?:\+[0-9A-Za-z.-]+)?$/i;

function isSemverTag(tag: string): boolean {
  return SEMVER_RE.test(tag);
}

function compareSemverDesc(a: string, b: string): number {
  const parse = (t: string) => {
    const clean = t.replace(/^v/i, '');
    const [main, pre = ''] = clean.split('-');
    const [maj, min, pat] = main.split('.').map(n => parseInt(n, 10));
    return { maj, min, pat, pre };
  };
  const ta = parse(a);
  const tb = parse(b);
  if (ta.maj !== tb.maj) return tb.maj - ta.maj;
  if (ta.min !== tb.min) return tb.min - ta.min;
  if (ta.pat !== tb.pat) return tb.pat - ta.pat;
  // Stable (no prerelease) should come before prerelease in desc order
  const aIsStable = ta.pre === '';
  const bIsStable = tb.pre === '';
  if (aIsStable && !bIsStable) return -1;
  if (!aIsStable && bIsStable) return 1;
  return (tb.pre || '').localeCompare(ta.pre || '');
}

const useStyles = makeStyles(theme => ({
  formControl: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  select: {
    '& .MuiSelect-select': {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
  },
  menuItem: {
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.common.white,
    },
  },
  label: {
    backgroundColor: theme.palette.background.paper,
    padding: '0 4px',
    transform: 'translate(14px, -6px) scale(0.75)',
  },
  helperText: {
    marginTop: theme.spacing(0.5),
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: theme.spacing(1),
    height: '48px',
  },
}));

interface HarborImageOptions {
  repository?: string; // Optional explicit repository override
  project?: string;    // Harbor project (e.g., buildpack-public)
  host?: string;       // Optional Harbor API host override
  placeholder?: string;
}

export const HarborImageList = ({
  onChange,
  rawErrors,
  required,
  formData,
  uiSchema,
  formContext,
}: FieldExtensionComponentProps<string>) => {
  const classes = useStyles();
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);
  const options = (uiSchema['ui:options'] as HarborImageOptions) || {};
  const { repository, project, host, placeholder } = options;
  
  // Determine user and app name for default repository construction
  const [userEntity, setUserEntity] = React.useState<any>(null);
  const appName: string | undefined = formContext?.formData?.name;
  
  React.useEffect(() => {
    identityApi.getBackstageIdentity().then(identity => {
      const ref = identity.userEntityRef || '';
      const parts = ref.split('/');
      setUserEntity(parts[parts.length - 1] || ''); // username is after last '/'
    });
  }, [identityApi]);
  
  // Construct dynamic repository path (no harbor_image_name in new templates):
  // - Prefer explicit ui:options.repository if provided
  // - Otherwise default to <appName>-<username> when available
  // - Fallback to undefined to skip fetching until identity resolves
  const dynamicRepository = (repository && repository.length > 0)
    ? repository
    : (userEntity && appName ? `${appName}${appName.endsWith('-') ? '' : '-'}${userEntity}` : undefined);
  
  // Debug logging
  console.log('HarborImageList Debug:', {
    userEntity,
    appName,
    repository,
    dynamicRepository,
    project,
    formContext: formContext?.formData
  });

  const { loading, error, value: tagOptions } = useAsync(async () => {
    if (!dynamicRepository || !project) return [];

    const backendUrl = configApi.getString('backend.baseUrl');

    const buildUrl = (repoParam: string) => {
      let u = `${backendUrl}/api/harbor/artifacts?project=${project}&repository=${repoParam}&with_tag=true`;
      if (host) u = u.concat(`&host=${host}`);
      return u;
    };

    const repoCandidates = Array.from(new Set([
      dynamicRepository,
      dynamicRepository.toLowerCase(),
    ]));
    const urlsToTry = repoCandidates.flatMap(r => [buildUrl(encodeURIComponent(r)), buildUrl(r)]);

    console.log('Harbor API Request (trying in order):', {
      project,
      dynamicRepository,
      repoCandidates,
      urlsToTry,
    });

    let artifacts: any[] = [];
    for (const url of urlsToTry) {
      try {
        const response = await fetchApi.fetch(url);
        if (!response.ok) {
          console.warn('Harbor API non-OK response:', response.status, response.statusText, 'for', url);
          continue;
        }
        const json = await response.json();
        console.debug('Harbor API response for', url, ':', json);
        if (json && typeof json === 'object' && (json as any).error) {
          console.warn('Harbor API error payload:', (json as any).error, 'for', url);
          continue;
        }
        artifacts = Array.isArray(json)
          ? json
          : (json && Array.isArray((json as any).artifacts))
            ? (json as any).artifacts
            : json
              ? [json]
              : [];
        if (artifacts.length) break;
      } catch (e) {
        console.warn('Harbor API fetch error for URL', url, e);
      }
    }

    if (!artifacts.length) {
      return [];
    }

    console.debug('Harbor parsed artifacts count:', artifacts.length);

    let tags: string[] = [];
    if (
      artifacts.length > 0 &&
      typeof artifacts[0] === 'object' &&
      artifacts[0] !== null &&
      'tag' in (artifacts[0] as any)
    ) {
      // Backend returns a flattened list with a `tag` field per item
      tags = (artifacts as any[])
        .map(a => (a && typeof (a as any).tag === 'string' ? (a as any).tag : ''))
        .filter((t): t is string => typeof t === 'string' && t.length > 0 && t !== 'undefined');
      console.debug('Harbor parsed tag candidates (flattened shape):', tags);
    } else {
      // Backend returns Harbor artifacts with nested tags
      tags = artifacts
        .filter(artifact => artifact && Array.isArray((artifact as any).tags))
        .flatMap(artifact => (artifact as any).tags)
        .map(tag => typeof tag === 'string' ? tag : (tag && typeof tag === 'object' && 'name' in (tag as any) ? (tag as any).name : ''))
        .filter((tag): tag is string => typeof tag === 'string' && tag.length > 0 && tag !== 'undefined');
      console.debug('Harbor parsed tag candidates (nested shape):', tags);
    }

    console.debug('Harbor parsed tag candidates:', tags);

    if (!tags.length) {
      return [];
    }

    const unique = Array.from(new Set(tags));
    const semverOnly = unique.filter(t => isSemverTag(t)).sort(compareSemverDesc);
    console.debug('Harbor final tagOptions (semver-only):', semverOnly);
    return semverOnly;

  }, [dynamicRepository, project, host, configApi, fetchApi, userEntity, appName]);

  return (
    <FormControl 
      className={classes.formControl}
      required={required} 
      error={!!rawErrors}
      variant="outlined"
    >
      <InputLabel 
        className={classes.label}
        shrink
      >
        Docker Image Tag
      </InputLabel>
      {loading ? (
        <div className={classes.loading}>
          <CircularProgress size={20} />
        </div>
      ) : error ? (
        <FormHelperText error>{error.message}</FormHelperText>
      ) : (
        <Select
          className={classes.select}
          value={formData || ''}
          onChange={e => onChange((e.target as HTMLSelectElement).value)}
          displayEmpty
          variant="outlined"
        >
          <MenuItem value="" className={classes.menuItem}>
            <em>{placeholder || 'Select a tag'}</em>
          </MenuItem>
          {(tagOptions || []).map(option => (
            <MenuItem 
              key={option} 
              value={option}
              className={classes.menuItem}
            >
              {option}
            </MenuItem>
          ))}
        </Select>
      )}
      <FormHelperText className={classes.helperText}>
        Select a tag from the available images in Harbor
      </FormHelperText>
    </FormControl>
  );
};
